import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { apiJson } from "../lib/queryClient";
import TrackMap from "../components/TrackMap";
import {
  groupDjiAnalyses,
  isLikelyTestStart,
  dronesCompatible,
  type DjiAnalyzeResult,
  type DjiAnalysisGroup,
} from "@client/lib/djiLogCore";
import { reverseGeocodePlace } from "@client/lib/countryMaps";

const MAX_DJI_LOG_BYTES = 15 * 1024 * 1024;

type LogbookEntry = {
  id: string;
  droneName?: string;
  flightDate?: string;
  locationName?: string | null;
  takeoffTime?: string | null;
  latitude?: number;
  longitude?: number;
};

type BulkError = { fileName: string; error: string };
type Selection = { target: "new" | "existing"; entryId?: string };

function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

type EntryMatch = {
  entry: LogbookEntry;
  timeDiffMs: number | null;
  distKm: number | null;
  droneOk: boolean;
  droneMatch: boolean;
  score: number;
  isSuggested: boolean;
  isStrong: boolean;
};

function computeMatches(group: DjiAnalysisGroup, entries: LogbookEntry[]): EntryMatch[] {
  const scored: EntryMatch[] = entries.map((e) => {
    const entryStart = e.takeoffTime
      ? Date.parse(e.takeoffTime)
      : e.flightDate
        ? Date.parse(e.flightDate)
        : NaN;
    const haveTimes = group.startTs != null && Number.isFinite(entryStart);
    const timeDiffMs = haveTimes ? Math.abs(group.startTs! - entryStart) : null;
    let distKm: number | null = null;
    if (
      group.startLat != null &&
      group.startLon != null &&
      e.latitude != null &&
      e.longitude != null &&
      Number.isFinite(e.latitude) &&
      Number.isFinite(e.longitude)
    ) {
      distKm = haversineKm(group.startLat, group.startLon, e.latitude, e.longitude);
    }
    const droneOk = dronesCompatible(group.droneName, e.droneName);
    const droneKnown = !!group.droneName && !!e.droneName;
    const droneMatch = droneKnown && droneOk;
    let score = 0;
    if (timeDiffMs != null) score += Math.max(0, 200 - timeDiffMs / 3_600_000);
    if (distKm != null) score += Math.max(0, 100 - distKm * 5);
    if (droneMatch) score += 150;
    const closeInTime = timeDiffMs != null && timeDiffMs <= 3 * 3_600_000;
    const closeInSpace = distKm != null && distKm <= 2;
    const isStrong =
      droneOk &&
      timeDiffMs != null &&
      timeDiffMs <= 60 * 60 * 1000 &&
      distKm != null &&
      distKm <= 0.5;
    return {
      entry: e,
      timeDiffMs,
      distKm,
      droneOk,
      droneMatch,
      score,
      isSuggested: droneOk && (closeInTime || closeInSpace),
      isStrong,
    };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored;
}

function matchDetail(m: EntryMatch): string {
  const parts: string[] = [];
  if (m.timeDiffMs != null) {
    const h = m.timeDiffMs / 3_600_000;
    parts.push(
      h < 1 ? `${Math.round(m.timeDiffMs / 60000)} min` : `±${h.toFixed(1).replace(".", ",")} h`,
    );
  }
  if (m.distKm != null) {
    parts.push(
      m.distKm < 1
        ? `${Math.round(m.distKm * 1000)} m`
        : `${m.distKm.toFixed(1).replace(".", ",")} km`,
    );
  }
  return parts.join(" · ");
}

const MIN_PLAUSIBLE_TS = Date.UTC(2000, 0, 1);

function plausibleTs(ts?: number | null): ts is number {
  return ts != null && Number.isFinite(ts) && ts >= MIN_PLAUSIBLE_TS;
}

function parseFileNameTs(fileName?: string): number | null {
  if (!fileName) return null;
  const m = fileName.match(/(\d{4})-(\d{2})-(\d{2})(?:[_ T]*\[?(\d{2})-(\d{2})-(\d{2})\]?)?/);
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss] = m;
  const dt = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    hh ? Number(hh) : 0,
    mm ? Number(mm) : 0,
    ss ? Number(ss) : 0,
  );
  const t = dt.getTime();
  return plausibleTs(t) ? t : null;
}

function groupDisplayTs(group: DjiAnalysisGroup): number | null {
  if (plausibleTs(group.startTs)) return group.startTs;
  for (const it of group.items) {
    const fromName = parseFileNameTs(it.meta.fileName);
    if (fromName != null) return fromName;
  }
  return null;
}

function fmtDateTime(ts?: number | null): string {
  if (!plausibleTs(ts)) return "Ohne Zeitstempel";
  try {
    return new Date(ts).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Ohne Zeitstempel";
  }
}

function logSummary(r: DjiAnalyzeResult): string {
  const m = r.meta;
  const parts: string[] = [];
  if (m.durationSec != null) parts.push(`${Math.round(m.durationSec)} s`);
  if (m.maxAltitudeM != null) parts.push(`${m.maxAltitudeM.toFixed(0)} m`);
  if (m.totalDistanceM != null) parts.push(`${(m.totalDistanceM / 1000).toFixed(2)} km`);
  return parts.join(" · ");
}

async function uploadAndAnalyze(file: File): Promise<DjiAnalyzeResult> {
  const { uploadURL } = await apiJson<{ uploadURL: string }>("POST", "/api/objects/upload");
  const put = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": "text/plain" },
  });
  if (!put.ok) throw new Error("Upload ins Object Storage fehlgeschlagen");
  return apiJson<DjiAnalyzeResult>("POST", "/api/dji-log/analyze", {
    uploadURL,
    fileName: file.name,
    sizeBytes: file.size,
  });
}

export default function DjiBulkImport() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: entries = [] } = useQuery<LogbookEntry[]>({ queryKey: ["/api/logbook"] });

  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; fileName: string } | null>(
    null,
  );
  const [results, setResults] = useState<DjiAnalyzeResult[]>([]);
  const [errors, setErrors] = useState<BulkError[]>([]);
  const [skippedTestStarts, setSkippedTestStarts] = useState(0);
  const [selection, setSelection] = useState<Record<string, Selection>>({});
  const [assignedTo, setAssignedTo] = useState<Record<string, string>>({});
  const [busyGroup, setBusyGroup] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [placeNames, setPlaceNames] = useState<Record<string, string>>({});
  const requestedPlaces = useRef<Set<string>>(new Set());

  const groups = useMemo(() => groupDjiAnalyses(results, { maxKm: 2, maxHours: 3 }), [results]);

  const matchesByGroup = useMemo(() => {
    const map: Record<string, EntryMatch[]> = {};
    for (const g of groups) map[g.id] = computeMatches(g, entries);
    return map;
  }, [groups, entries]);

  useEffect(() => {
    groups.forEach((g) => {
      if (g.startLat == null || g.startLon == null) return;
      if (placeNames[g.id] || requestedPlaces.current.has(g.id)) return;
      requestedPlaces.current.add(g.id);
      reverseGeocodePlace(g.startLat, g.startLon)
        .then((name) => {
          if (name) setPlaceNames((prev) => ({ ...prev, [g.id]: name }));
          else requestedPlaces.current.delete(g.id);
        })
        .catch(() => {
          requestedPlaces.current.delete(g.id);
        });
    });
  }, [groups, placeNames]);

  useEffect(() => {
    setSelection((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of groups) {
        if (next[g.id]) continue;
        const top = matchesByGroup[g.id]?.[0];
        if (top && top.isStrong) {
          next[g.id] = { target: "existing", entryId: top.entry.id };
        } else {
          next[g.id] = { target: "new" };
        }
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [groups, matchesByGroup]);

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;
    e.target.value = "";
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    setAnalyzing(true);
    setProgress(null);
    const collected: DjiAnalyzeResult[] = [];
    const collectedErrors: BulkError[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress({ done: i, total: files.length, fileName: file.name });
        if (file.size > MAX_DJI_LOG_BYTES) {
          collectedErrors.push({ fileName: file.name, error: "Datei zu groß (max. 15 MB)." });
          setProgress({ done: i + 1, total: files.length, fileName: file.name });
          continue;
        }
        try {
          collected.push(await uploadAndAnalyze(file));
        } catch (err) {
          collectedErrors.push({
            fileName: file.name,
            error: err instanceof Error ? err.message : String(err),
          });
        }
        setProgress({ done: i + 1, total: files.length, fileName: file.name });
      }
      const kept = collected.filter((r) => !isLikelyTestStart(r.meta));
      setResults(kept);
      setSkippedTestStarts(collected.length - kept.length);
      setErrors(collectedErrors);
      setSelection({});
      setAssignedTo({});
      setPlaceNames({});
      requestedPlaces.current.clear();
    } finally {
      setAnalyzing(false);
      setProgress(null);
    }
  }

  function setGroupSelection(groupId: string, sel: Selection) {
    setSelection((prev) => ({ ...prev, [groupId]: sel }));
  }

  async function assignGroupInternal(group: DjiAnalysisGroup): Promise<string | null> {
    const sel = selection[group.id] ?? { target: "new" };
    if (sel.target === "existing" && !sel.entryId) return null;
    let entryId = sel.entryId;
    if (sel.target === "new") {
      const created = await apiJson<{ id: string }>("POST", "/api/dji-log/assign", {
        objectPath: group.items[0].meta.objectPath,
        fileName: group.items[0].meta.fileName,
        sizeBytes: group.items[0].meta.sizeBytes,
        target: "new",
      });
      entryId = created.id;
    } else {
      await apiJson<{ id: string }>("POST", "/api/dji-log/assign", {
        objectPath: group.items[0].meta.objectPath,
        fileName: group.items[0].meta.fileName,
        sizeBytes: group.items[0].meta.sizeBytes,
        target: "existing",
        entryId,
      });
    }
    for (let i = 1; i < group.items.length; i++) {
      await apiJson<{ id: string }>("POST", "/api/dji-log/assign", {
        objectPath: group.items[i].meta.objectPath,
        fileName: group.items[i].meta.fileName,
        sizeBytes: group.items[i].meta.sizeBytes,
        target: "existing",
        entryId,
      });
    }
    return entryId ?? null;
  }

  async function assignGroup(group: DjiAnalysisGroup) {
    const sel = selection[group.id] ?? { target: "new" };
    if (sel.target === "existing" && !sel.entryId) return;
    setBusyGroup(group.id);
    try {
      const entryId = await assignGroupInternal(group);
      if (entryId) {
        setAssignedTo((prev) => ({ ...prev, [group.id]: entryId }));
        await queryClient.invalidateQueries({ queryKey: ["/api/logbook"] });
      }
    } catch (err) {
      setErrors((prev) => [
        ...prev,
        {
          fileName: group.items[0]?.meta.fileName ?? "Flug",
          error: err instanceof Error ? err.message : "Zuordnung fehlgeschlagen.",
        },
      ]);
    } finally {
      setBusyGroup(null);
    }
  }

  async function assignAll() {
    setBulkBusy(true);
    const newlyAssigned: Record<string, string> = {};
    const newErrors: BulkError[] = [];
    try {
      for (const g of groups) {
        if (assignedTo[g.id]) continue;
        try {
          const entryId = await assignGroupInternal(g);
          if (entryId) newlyAssigned[g.id] = entryId;
        } catch (err) {
          newErrors.push({
            fileName: g.items[0]?.meta.fileName ?? "Flug",
            error: err instanceof Error ? err.message : "Zuordnung fehlgeschlagen.",
          });
        }
      }
      if (Object.keys(newlyAssigned).length > 0) {
        setAssignedTo((prev) => ({ ...prev, ...newlyAssigned }));
        await queryClient.invalidateQueries({ queryKey: ["/api/logbook"] });
      }
      if (newErrors.length > 0) setErrors((prev) => [...prev, ...newErrors]);
    } finally {
      setBulkBusy(false);
    }
  }

  const assignedCount = Object.keys(assignedTo).length;
  const openCount = groups.filter((g) => !assignedTo[g.id]).length;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/logbuch" className="text-sm text-brand-600 hover:underline">
          ← Zurück zum Logbuch
        </Link>
        <h1 className="text-xl font-semibold mt-2">DJI-Logs im Bulk importieren</h1>
        <p className="text-sm text-[var(--color-app-text-secondary)] mt-1">
          Wähle mehrere DJI-Controller-Protokolle (.txt aus DJI Fly/GO/Pilot) auf einmal. Logs vom
          selben Startort (unter 2 km) und derselben Startzeit (unter 3 h) werden als ein Flug
          gruppiert — ideal bei Akku-Wechseln. Anschließend kannst du jeden Flug neu anlegen oder
          einem bestehenden Eintrag zuordnen.
        </p>
      </div>

      <div className="card">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={analyzing}
          className="btn-primary text-sm"
          data-testid="button-bulk-pick"
        >
          {analyzing ? "Wird ausgewertet …" : "Logs auswählen"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,text/plain"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid="input-bulk-file"
        />
        {analyzing && progress ? (
          <p className="text-xs text-[var(--color-app-text-secondary)] mt-2" data-testid="text-bulk-progress">
            Verarbeite {progress.done} / {progress.total} — {progress.fileName}
          </p>
        ) : null}
      </div>

      {errors.length > 0 ? (
        <div
          className="card border border-[var(--color-nogo)] bg-[var(--color-nogo-bg)]"
          data-testid="section-bulk-errors"
        >
          <h2 className="font-semibold text-[var(--color-nogo)] mb-2">
            {errors.length} {errors.length === 1 ? "Datei" : "Dateien"} fehlgeschlagen
          </h2>
          <ul className="space-y-1">
            {errors.map((er, i) => (
              <li key={`err-${i}`} className="text-xs text-[var(--color-nogo)]">
                {er.fileName}: {er.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {skippedTestStarts > 0 ? (
        <div className="card" data-testid="section-bulk-skipped">
          <h2 className="font-semibold text-[var(--color-app-text)] mb-1">
            {skippedTestStarts} Test-/Einstellstart{skippedTestStarts === 1 ? "" : "s"} übersprungen
          </h2>
          <p className="text-xs text-[var(--color-app-text-secondary)]">
            Sehr kurze Starts ohne zurückgelegte Strecke (Kalibrierung/Funktionstest) werden nicht
            als Flug erfasst.
          </p>
        </div>
      ) : null}

      {groups.length > 0 ? (
        <div className="card flex items-center justify-between gap-3 flex-wrap" data-testid="section-bulk-summary">
          <div className="text-sm text-[var(--color-app-text-secondary)]">
            {groups.length} {groups.length === 1 ? "Flug" : "Flüge"} erkannt · {assignedCount}{" "}
            zugeordnet · {openCount} offen
          </div>
          <button
            onClick={assignAll}
            disabled={bulkBusy || openCount === 0}
            className="btn-primary text-sm"
            data-testid="button-bulk-assign-all"
          >
            {bulkBusy ? "Wird zugeordnet …" : "Alle offenen zuordnen"}
          </button>
        </div>
      ) : null}

      {groups.map((group, index) => {
        const sel = selection[group.id] ?? { target: "new" };
        const matches = matchesByGroup[group.id] ?? [];
        const suggested = matches.filter((m) => m.isSuggested).slice(0, 4);
        const assignedEntryId = assignedTo[group.id];
        const busy = busyGroup === group.id;

        const hasCoords = group.startLat != null && group.startLon != null;
        const coordsText = hasCoords
          ? `${group.startLat!.toFixed(4)}, ${group.startLon!.toFixed(4)}`
          : "Kein GPS-Startpunkt";
        const placeName = placeNames[group.id];
        const displayTs = groupDisplayTs(group);

        const firstTrack =
          group.items
            .map((it) => (it.track && it.track.length > 0 ? it.track : it.meta.track ?? []))
            .find((t) => t.length >= 2) ?? [];
        const firstPoint = firstTrack.length > 0 ? firstTrack[0] : undefined;

        return (
          <div className="card" key={group.id} data-testid={`group-dji-bulk-${index}`}>
            <h2 className="font-semibold mb-1">
              Flug {index + 1} · {group.items.length} {group.items.length === 1 ? "Log" : "Logs"}
            </h2>
            <p className="text-sm text-[var(--color-app-text)]">{group.droneName || "Unbekanntes Fluggerät"}</p>
            <p className="text-xs text-[var(--color-app-text-secondary)]">{fmtDateTime(displayTs)}</p>
            <p className="text-xs text-[var(--color-app-text-secondary)]">{placeName || coordsText}</p>
            {placeName && hasCoords ? (
              <p className="text-xs text-[var(--color-app-text-tertiary)]">{coordsText}</p>
            ) : null}

            {firstTrack.length >= 2 ? (
              <div className="mt-3">
                <TrackMap
                  key={`bulk-track-${group.id}-${firstTrack.length}`}
                  track={firstTrack}
                  fallbackLat={firstPoint?.lat}
                  fallbackLon={firstPoint?.lon}
                  height={200}
                />
              </div>
            ) : (
              <p className="text-xs text-[var(--color-app-text-secondary)] mt-2">
                Kein GPS-Track verfügbar (verschlüsseltes Log oder keine Positionsdaten).
              </p>
            )}

            <ul className="mt-3 space-y-1">
              {group.items.map((it, i) => (
                <li
                  key={`${group.id}-log-${i}`}
                  className="flex items-center justify-between gap-2 border border-[var(--color-app-border)] rounded-md px-2 py-1.5"
                >
                  <span className="text-xs text-[var(--color-app-text)] truncate">{it.meta.fileName}</span>
                  <span className="text-xs text-[var(--color-app-text-tertiary)] whitespace-nowrap">
                    {logSummary(it) || "Keine Kennzahlen"}
                  </span>
                </li>
              ))}
            </ul>

            {assignedEntryId ? (
              <button
                onClick={() => navigate(`/logbuch/${assignedEntryId}`)}
                className="btn-secondary text-sm mt-3"
                data-testid={`button-open-entry-${index}`}
              >
                Zugeordnet — Flug öffnen
              </button>
            ) : (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold uppercase text-[var(--color-app-text-secondary)]">Zuordnen zu</p>

                <label
                  className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer ${
                    sel.target === "new"
                      ? "border-brand-500 bg-brand-50"
                      : "border-[var(--color-app-border)]"
                  }`}
                  data-testid={`choice-new-${index}`}
                >
                  <input
                    type="radio"
                    name={`target-${group.id}`}
                    checked={sel.target === "new"}
                    onChange={() => setGroupSelection(group.id, { target: "new" })}
                  />
                  <span className="text-sm text-[var(--color-app-text)]">Neuer Flug</span>
                </label>

                {suggested.map((m) => {
                  const active = sel.target === "existing" && sel.entryId === m.entry.id;
                  const title = m.entry.locationName || m.entry.flightDate || "Flug";
                  return (
                    <label
                      key={`${group.id}-match-${m.entry.id}`}
                      className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer ${
                        active ? "border-brand-500 bg-brand-50" : "border-[var(--color-app-border)]"
                      }`}
                      data-testid={`choice-existing-${index}-${m.entry.id}`}
                    >
                      <input
                        type="radio"
                        name={`target-${group.id}`}
                        checked={active}
                        onChange={() =>
                          setGroupSelection(group.id, { target: "existing", entryId: m.entry.id })
                        }
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-[var(--color-app-text)] truncate">{title}</span>
                        <span className="block text-xs text-[var(--color-app-text-tertiary)]">
                          {matchDetail(m) || "Bestehender Flug"}
                          {m.isStrong ? "  ·  passt genau" : ""}
                        </span>
                      </span>
                    </label>
                  );
                })}

                <button
                  onClick={() => assignGroup(group)}
                  disabled={busy}
                  className="btn-primary text-sm"
                  data-testid={`button-assign-group-${index}`}
                >
                  {busy ? "Wird zugeordnet …" : "Zuordnen"}
                </button>
              </div>
            )}
          </div>
        );
      })}

      {!analyzing && results.length === 0 ? (
        <p className="text-sm text-[var(--color-app-text-secondary)]" data-testid="text-bulk-empty">
          {skippedTestStarts > 0
            ? "Nur Test-/Einstellstarts gefunden – keine Flüge zu importieren."
            : "Noch keine Logs ausgewählt."}
        </p>
      ) : null}
    </div>
  );
}
